'use client';

import { createContext, useContext, useState, useCallback } from 'react';
import type { FC, ReactNode } from 'react';
import cx from 'classnames';
import { nanoid } from 'nanoid';
import type { ToastNotifyOptions } from '@pyranger/types';
import { InformationCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';

import './toast-provider.css';

interface ToastState {
    options: ToastNotifyOptions;
    id: string;
}

/**
 * A function that triggers a toast message. If the second `id` parameter is provided, the same id
 * can be used to remove the toast manually. Whether the `id` is provided or not, the toast will be
 * removed after `options.duration` milliseconds, which defaults to 5000 ms.
 */
type ToastNotify = (options: ToastNotifyOptions, id?: string) => void;

interface ToastContext {
    notify: ToastNotify;
    remove(toastID: string): void;
}

const Context = createContext({
    notify: () => undefined,
    remove: () => undefined,
} as ToastContext);

/**
 * A context provider that supports a `useToast` hook. The `useToast` hook provides a `notify`
 * method which uses the `toast` and `alert` components from DaisyUI to show a toast message. It
 * also provides a `remove` method for removing a toast manually by its ID.
 */
export const ToastProvider: FC<{
    children: ReactNode;
}> = ({ children }) => {
    const [toasts, setToasts] = useState<ToastState[]>([]);

    const notify = useCallback((options: ToastNotifyOptions, id = nanoid()) => {
        const {
            duration = 5000,
            icon = <InformationCircleIcon width={24} height={24} />,
            message,
            variant = 'info',
        } = options;

        setToasts((prev) => [
            // Put new toasts at start of array so they render on top vertically
            {
                id,
                options: {
                    duration,
                    icon,
                    message,
                    variant,
                },
            },
            ...prev,
        ]);

        const timeoutID = setTimeout(() => {
            setToasts((prev) =>
                prev.filter((toastState) => toastState.id !== id),
            );
        }, duration);

        return () => {
            clearTimeout(timeoutID);
        };
    }, []);

    const remove = (id: string) =>
        setToasts((prev) => prev.filter((toastState) => toastState.id !== id));

    return (
        <Context.Provider
            value={{
                notify,
                remove,
            }}
        >
            {children}
            <div className="toast">
                {toasts.map((toastState) => (
                    <div
                        key={toastState.id}
                        role="alert"
                        className={cx(
                            'alert flex items-start justify-between gap-4',
                            {
                                'alert-info border-info-content':
                                    toastState.options.variant === 'info',
                                'alert-success border-success-content':
                                    toastState.options.variant === 'success',
                                'alert-warning border-warning-content':
                                    toastState.options.variant === 'warning',
                                'alert-error border-error-content':
                                    toastState.options.variant === 'error',
                            },
                        )}
                    >
                        <div className="flex items-start justify-start gap-2">
                            {toastState?.options?.icon}
                            {toastState.options.message}
                        </div>
                        <button
                            className={cx('h-6 w-6', {
                                'info-content':
                                    toastState.options.variant === 'info',
                                'success-content':
                                    toastState.options.variant === 'success',
                                'warning-content':
                                    toastState.options.variant === 'warning',
                                'error-content':
                                    toastState.options.variant === 'error',
                            })}
                            onClick={() => {
                                remove(toastState.id);
                            }}
                        >
                            <XMarkIcon
                                height={24}
                                width={24}
                                aria-hidden={false}
                            />
                            <span className="sr-only">Close</span>
                        </button>
                    </div>
                ))}
            </div>
        </Context.Provider>
    );
};

type UseToast = () => ToastContext;

export const useToast: UseToast = () => useContext(Context);
